import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { ExclamationMarkIcon, SyncIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { computeTripsMinMaxCapacity } from '../../../../api/centers/compute-trips-min-max-capacity'
import { suggestTripPlaceValues } from '../../../../api/centers/suggest-trip-places-values'
import { AssignJourneyToCenterInput } from '../../../../lib/types/models/center/center-input'
import { CenterProperties } from '../../../../lib/types/models/center/properties.center'
import { CenterToJourneyProperties } from '../../../../lib/types/models/center-to-journey/properties.center-to-journey'
import { TripPlaceToCenterRequestInput } from '../../../../lib/types/models/trip-place-to-center-request/trip-place-to-center-request-input'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import ValidationButtons, {
  ValidationButtonsBaseFormProps
} from '../../../elements/validation-buttons'
import JourneyCardHeader from './journey-card-header'
import StaffQualificationStatisticsCard from './staff-qualification-statistics-card'
import TotalNumberOfChildren from './total-number-of-children'
import TotalNumberOfStaffs from './total-number-of-staffs'
import TripDetails from './trip-details'

type Props = ValidationButtonsBaseFormProps<AssignJourneyToCenterInput> & {
  checkForErrors: boolean
}

const CenterTripsInformation = ({ buttonsConfig, checkForErrors }: Props) => {
  const form = useFormContext<AssignJourneyToCenterInput>()

  const { frenchStatistics, frenchTotal } =
    CenterProperties.getFrenchQualificationStaffsStatistics(form.watch('staffRequests'))
  const { foreignStatistics, foreignTotal } =
    CenterProperties.getForeignQualificationStaffsStatistics(form.watch('staffRequests'))

  const centerToJourneys = form.watch('centerToJourneys')
  const tripData = form.watch('tripPlacesRequests')

  const { data } = useQuery({
    queryKey: ['compute-trips-min-max-capacity', centerToJourneys],
    queryFn: async () => {
      const res = await computeTripsMinMaxCapacity(
        CenterToJourneyProperties.mapCenterToJourneyInputToRequest(centerToJourneys)
      )
      const previousTrips = form.watch('tripPlacesRequests')
      // previousTrips[0].
      const allTrips = centerToJourneys
        .flatMap((item) => item.relatedTrips)
        .filter((item, index, self) => self.findIndex((t) => t.id === item.id) === index)

      form.setValue(
        'tripPlacesRequests',
        res.map((item) => {
          const previousTrip = previousTrips.find((t) => t.trip.id === item.tripId)
          const trip = allTrips.find((t) => t.id === item.tripId)
          if (!trip) throw new Error(`Trip with id ${item.tripId} not found`)
          return {
            trip,
            numberOfStaffPlacesRequested: previousTrip?.numberOfStaffPlacesRequested ?? 0,
            numberOfStudentsPlacesRequested: previousTrip?.numberOfStudentsPlacesRequested ?? 0,
            disableRestriction: previousTrip?.disableRestriction ?? false
          }
        })
      )

      return res
    }
  })

  const { refetch, isFetching: isSuggestingTripPlaceValues } = useQuery({
    queryKey: ['suggest-trip-places-values', centerToJourneys, frenchTotal],
    queryFn: async () => {
      // The trips only carry the French staffs: the staff split must add up to their total
      const suggestedTripPlaces = await suggestTripPlaceValues({
        centerToJourneys:
          CenterToJourneyProperties.mapCenterToJourneyInputToRequest(centerToJourneys),
        numberOfStaffs: frenchTotal
      })
      if (suggestedTripPlaces) {
        const trips: TripPlaceToCenterRequestInput[] = tripData.map((item) => {
          const suggestedTrip = suggestedTripPlaces.find((t) => t.tripId === item.trip.id)
          if (!suggestedTrip) return item
          return {
            ...item,
            numberOfStaffPlacesRequested: suggestedTrip.staffsRequired,
            numberOfStudentsPlacesRequested: suggestedTrip.places,
            disableRestriction: item.disableRestriction ?? false
          }
        })
        form.setValue('tripPlacesRequests', trips)
      }
      return suggestedTripPlaces ?? []
    },
    enabled: false // Query will not run automatically on mount
  })

  const numberOfStudentsErrors = tripData
    .map((request) => {
      if (data)
        return CenterProperties.checkNumberOfStudentsAccordingToTripCapacity(
          request,
          CenterProperties.getMinMaxCapacityForTrip(data, request.trip.id).minNumberOfStudents,
          CenterProperties.getMinMaxCapacityForTrip(data, request.trip.id).maxNumberOfStudents
        )
    })
    .filter(Boolean)

  const errorMessage = conditionalRender(
    checkForErrors,
    [CenterProperties.checkPossibleErrorsPerStep(3, form.watch()), numberOfStudentsErrors[0]]
      .filter(Boolean)
      .join(' | ')
  )

  if (!data) return <Loader center />

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-3">
        <TotalNumberOfChildren />
        <TotalNumberOfStaffs />
      </div>
      <div className="flex flex-col gap-4">
        {centerToJourneys.map((centerToJourney) => (
          <JourneyCardHeader
            key={centerToJourney.journey.id}
            centerToJourney={centerToJourney}
            mode="view"
          />
        ))}
      </div>
      <div className="flex gap-3">
        {frenchTotal > 0 && (
          <StaffQualificationStatisticsCard
            title="Encadrants français : "
            variant="fushia"
            total={frenchTotal}
            mapping={frenchStatistics}
          />
        )}

        {foreignTotal > 0 && (
          <StaffQualificationStatisticsCard
            title="Encadrants etrangers : "
            variant="purple"
            total={foreignTotal}
            mapping={foreignStatistics}
          />
        )}
      </div>
      <Button
        type="button"
        variant="secondary"
        label="Suggérer la répartition"
        iconColor={tailwindColors.primary.DEFAULT}
        icon={(props) => (
          <SyncIcon
            {...props}
            className={cn('h-4 w-4', { 'animate-spin': isSuggestingTripPlaceValues })}
          />
        )}
        disabled={isSuggestingTripPlaceValues}
        onClick={async () => {
          // Trigger the query manually on button click
          const { error } = await refetch()
          // The backend says why no split could be suggested, instead of leaving the trips at 0/0
          if (error) showAxiosError(error)
        }}
      />
      {errorMessage && (
        <Badge
          variant="danger"
          className="py-5 rounded"
          icon={
            <div className="border border-red rounded-md w-fit p-0.5 rounded-full">
              <ExclamationMarkIcon color={tailwindColors.red.DEFAULT} />
            </div>
          }
          iconPosition="start"
          text={errorMessage}
        />
      )}
      {tripData.map((tripPlaceToCenterRequest) => (
        <TripDetails
          key={tripPlaceToCenterRequest.trip.id}
          checkForErrors={checkForErrors}
          tripPlaceToCenterRequest={tripPlaceToCenterRequest}
          onTripToCenterRequestChange={(value) => {
            // If "no supervisor required" is checked on this trip (staff is undefined),
            // remove it from all other trips
            const isNoSupervisorChecked = value.numberOfStaffPlacesRequested === undefined

            form.setValue(
              'tripPlacesRequests',
              tripData.map((item) => {
                if (item.trip.id === tripPlaceToCenterRequest.trip.id) {
                  return value
                }
                // If this trip is being set to no supervisor, ensure other trips have staff defined
                if (isNoSupervisorChecked && item.numberOfStaffPlacesRequested === undefined) {
                  return { ...item, numberOfStaffPlacesRequested: 0 }
                }
                return item
              })
            )
          }}
          maxNumberOfStudents={
            CenterProperties.getMinMaxCapacityForTrip(data, tripPlaceToCenterRequest.trip.id)
              .maxNumberOfStudents
          }
          minNumberOfStudents={
            CenterProperties.getMinMaxCapacityForTrip(data, tripPlaceToCenterRequest.trip.id)
              .minNumberOfStudents
          }
        />
      ))}
      <ValidationButtons buttonsConfig={buttonsConfig} preventSubmit={!!errorMessage} />
    </div>
  )
}

export default CenterTripsInformation
